import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { ArrowLeft, ChevronDown, FileText, Download, Filter } from 'lucide-react'
import setThemeColor from '../lib/themeColor'
import useOrgSettings from '../lib/useOrgSettings'
import useEventRole from '../lib/useEventRole'

// ── Dossier theme — mirrors FragoDetail for visual continuity ─────────
const D = {
  page: { minHeight: '100vh', background: '#d8c898', color: '#1f1d18', fontFamily: "'Special Elite', 'Courier Prime', monospace" },
  nav: { background: '#1f1d18', borderBottom: '3px solid #c08a2a', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  paper: { background: '#f0e6c8', border: '1px solid rgba(120,100,60,.35)', boxShadow: '0 2px 8px rgba(0,0,0,.18), 0 1px 3px rgba(0,0,0,.12)', position: 'relative', overflow: 'hidden' },
  paperLines: { position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 26px, rgba(0,0,0,.025) 26px, rgba(0,0,0,.025) 27px)' },
  display: { fontFamily: "'Stardos Stencil', 'Special Elite', serif" },
  body: { fontFamily: "'Special Elite', 'Courier Prime', monospace" },
  ink: { color: '#1f1d18' }, ink2: { color: '#4a4636' }, ink3: { color: '#7a7058' }, stamp: { color: '#c08a2a' },
  // Section tints used for highlight callouts
  red:  '#a82a1f', blue: '#2a4a8c', green: '#3a6a3a',
}

const EVENT_TYPE_COLOR = {
  kill_confirmed: D.red, kill_unconfirmed: '#7a7058', team_kill: D.red, faction_kill: D.red,
  bounty_issued: D.blue, bounty_claimed: D.green,
  frago_issued: '#c08a2a', frago_delegated: D.blue, frago_accepted: D.green,
  frago_completed: D.green, frago_failed: D.red, frago_resent: '#c08a2a',
  intel_found: D.blue,
  game_pause: D.ink3, game_resume: D.ink3,
  phase_live: D.green, phase_endex: D.red, phase_closed: D.ink3,
  scheduled_deployment: D.blue,
}

// Toggleable section header
function SectionHeader({ num, label, collapsed, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        ...D.display, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: '#4a4636', borderBottom: '1px solid #cbb98c', paddingBottom: 4, marginBottom: 8, marginTop: 16,
      }}
    >
      <span>§ {String(num).padStart(2, '0')} — {label}</span>
      <ChevronDown size={14} style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .2s', color: '#7a7058' }} />
    </button>
  )
}

const fmtDuration = (mins) => {
  if (!mins || mins <= 0) return '—'
  const h = Math.floor(mins / 60), m = Math.round(mins % 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—'
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'

export default function EventAAR() {
  const { id: eventId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [aar, setAar] = useState(null)
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [activeTab, setActiveTab] = useState('brief')  // 'brief' | 'detail'
  const [collapsed, setCollapsed] = useState({})       // section_id → bool
  const [exporting, setExporting] = useState(false)
  // Score-hiding decision is org × role: GM/m55 see everything; everyone
  // else gets their points stripped when the org sets the flag.
  const { hideFactionScores } = useOrgSettings(eventId)
  const role = useEventRole(eventId)
  const hideScores = hideFactionScores && !role.isFullGM

  // Detail-tab timeline filters
  const [filterType, setFilterType] = useState('all')
  const [filterFaction, setFilterFaction] = useState('all')

  useEffect(() => { setThemeColor('dossier') }, [])
  useEffect(() => { fetchAAR() }, [eventId])

  const fetchAAR = async () => {
    setLoading(true); setErr(null)
    const [{ data: ev }, { data: aarData, error }, { data: blindLog }] = await Promise.all([
      supabase.from('events').select('id, name, status, closed_at, location, org_id').eq('id', eventId).limit(1),
      supabase.rpc('get_event_aar', { p_event_id: eventId, p_user_id: user.id }),
      supabase.from('blind_man_log')
        .select('called_by, reason, called_at, resumed_at, resumed_by, duration_seconds, called_user:users!blind_man_log_called_by_fkey(callsign, display_name), resumed_user:users!blind_man_log_resumed_by_fkey(callsign, display_name)')
        .eq('event_id', eventId)
        .order('called_at'),
    ])
    if (error) {
      setErr(error.message || 'Failed to load AAR')
      setLoading(false); return
    }
    if (!ev?.[0]) { navigate('/play'); return }
    setEvent(ev[0])

    const base = Array.isArray(aarData) ? aarData[0] : aarData
    // Merge blind_man_log rows into the timeline as synthetic entries so
    // ceasefires appear in chronological order alongside kills/FRAGOs.
    if (base && Array.isArray(blindLog) && blindLog.length > 0) {
      const extra = []
      blindLog.forEach(row => {
        const callerName = row.called_user?.callsign || row.called_user?.display_name || 'Staff'
        extra.push({
          timestamp: row.called_at,
          event_type: 'blind_man_called',
          description: `🔴 BLIND MAN — called by ${callerName}${row.reason ? ` · ${row.reason}` : ''}`,
        })
        if (row.resumed_at) {
          const resumeName = row.resumed_user?.callsign || row.resumed_user?.display_name || 'Staff'
          const dur = row.duration_seconds
          const durStr = dur != null
            ? `${Math.floor(dur / 60)}m ${dur % 60}s`
            : ''
          extra.push({
            timestamp: row.resumed_at,
            event_type: 'blind_man_lifted',
            description: `🟢 BLIND MAN LIFTED — by ${resumeName}${durStr ? ` · duration: ${durStr}` : ''}`,
          })
        }
      })
      const merged = [...(base.timeline || []), ...extra]
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      base.timeline = merged
    }
    setAar(base)
    setLoading(false)
  }

  const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }))

  // ── Derived helpers (memoized) ───────────────────────────────────────
  const fileNumber = useMemo(() => {
    if (!aar) return '—'
    const closed = aar.summary?.closed_at || aar.summary?.endex_at
    const datePart = closed ? new Date(closed).toISOString().slice(0, 10).replace(/-/g, '') : 'XXXXXXXX'
    const seq = (aar.id || '').slice(0, 4).toUpperCase()
    return `AAR-${datePart}-${seq}`
  }, [aar])

  const factionMap = useMemo(() => {
    const m = {}
    Object.entries(aar?.faction_summaries || {}).forEach(([fid, f]) => { m[fid] = f })
    return m
  }, [aar])

  const factionList = useMemo(() => Object.values(aar?.faction_summaries || {}), [aar])

  const filteredTimeline = useMemo(() => {
    const tl = aar?.timeline || []
    return tl.filter(e => {
      if (filterType !== 'all') {
        if (filterType === 'kills' && !e.event_type?.startsWith('kill') && e.event_type !== 'team_kill' && e.event_type !== 'faction_kill') return false
        if (filterType === 'fragos' && !e.event_type?.startsWith('frago')) return false
        if (filterType === 'bounties' && !e.event_type?.startsWith('bounty')) return false
        if (filterType === 'intel' && e.event_type !== 'intel_found') return false
        if (filterType === 'phase' && !e.event_type?.startsWith('phase') && e.event_type !== 'game_pause' && e.event_type !== 'game_resume') return false
      }
      if (filterFaction !== 'all' && e.faction_id !== filterFaction) return false
      return true
    })
  }, [aar, filterType, filterFaction])

  if (loading) return (
    <div style={{ ...D.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ ...D.display, fontSize: 14, color: '#7a7058', letterSpacing: '0.14em' }}>RETRIEVING AAR...</p>
    </div>
  )
  if (err || !aar) return (
    <div style={{ ...D.page, padding: '40px 20px', textAlign: 'center' }}>
      <p style={{ ...D.display, fontSize: 16, color: '#a82a1f', letterSpacing: '0.14em' }}>AAR UNAVAILABLE</p>
      <p style={{ ...D.body, fontSize: 12, color: '#7a7058', marginTop: 8 }}>{err || 'No AAR exists for this event yet.'}</p>
      <button onClick={() => navigate(-1)} style={{ marginTop: 16, ...D.body, fontSize: 11, color: '#c08a2a', background: 'none', border: '1px solid #c08a2a', padding: '8px 16px', cursor: 'pointer', letterSpacing: '0.14em', textTransform: 'uppercase' }}>BACK</button>
    </div>
  )

  const isFull = !!aar.is_full_view
  const viewerFactionId = aar.viewer_faction_id
  const viewerFaction = viewerFactionId ? factionMap[viewerFactionId] : null
  const classificationLabel = isFull
    ? (aar.reveal_setting === 'immediate' || (aar.reveal_unlocks_at && new Date(aar.reveal_unlocks_at) <= new Date()) ? 'DECLASSIFIED' : 'UNRESTRICTED')
    : `CLASSIFIED — ${(viewerFaction?.name || 'FACTION').toUpperCase()} EYES ONLY`

  const exportPdf = async () => {
    if (exporting) return
    setExporting(true)
    try {
      const { downloadAarPdf } = await import('../lib/aarPdf')
      await downloadAarPdf({ aar, event, fileNumber, classificationLabel })
    } catch (e) {
      console.error('[EventAAR] PDF export failed', e)
      alert('PDF export failed: ' + (e?.message || 'unknown'))
    } finally {
      setExporting(false)
    }
  }

  const summary = aar.summary || {}

  return (
    <div style={D.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Stardos+Stencil:wght@400;700&family=Special+Elite&family=Courier+Prime:wght@400;700&display=swap');`}</style>

      {/* NAV */}
      <div style={D.nav}>
        <div>
          <div style={{ ...D.display, color: '#f0e6c8', fontSize: 14, letterSpacing: '0.14em' }}>ANUBIS</div>
          <div style={{ color: '#c08a2a', fontSize: 9, letterSpacing: '0.12em', marginTop: 2 }}>FIELD OPS · AAR</div>
        </div>
        <div style={{ color: '#c08a2a', fontSize: 9, letterSpacing: '0.08em' }}>
          {event?.name?.toUpperCase() || 'NO EVENT'}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px' }}>
        <button onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#7a7058', fontFamily: "'Special Elite', monospace", fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          <ArrowLeft size={14} /> RETURN
        </button>
        <button onClick={exportPdf} disabled={exporting}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid #c08a2a', color: '#c08a2a', fontFamily: "'Stardos Stencil', monospace", fontSize: 11, cursor: exporting ? 'wait' : 'pointer', letterSpacing: '0.14em', textTransform: 'uppercase', padding: '6px 12px', opacity: exporting ? 0.6 : 1 }}>
          <Download size={12} /> {exporting ? 'GENERATING...' : 'DOWNLOAD PDF'}
        </button>
      </div>

      <div style={{ margin: '0 10px 16px', maxWidth: 920, marginInline: 'auto', ...D.paper }}>
        <div style={D.paperLines} />

        {/* Header strip */}
        <div style={{ padding: '14px 16px', borderBottom: '2px solid #c08a2a', position: 'relative' }}>
          {/* Classification stamp */}
          <div style={{
            display: 'inline-block', ...D.display, fontSize: 11, letterSpacing: '0.18em',
            padding: '3px 10px', border: `2px solid ${isFull ? '#3a6a3a' : '#a82a1f'}`,
            color: isFull ? '#3a6a3a' : '#a82a1f',
            transform: 'rotate(-1.5deg)', marginBottom: 8,
          }}>
            ● {classificationLabel}
          </div>
          <div style={{ ...D.display, fontSize: 24, color: '#1f1d18', letterSpacing: '0.05em' }}>
            <FileText size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8, color: '#c08a2a' }} />
            AFTER ACTION REPORT
          </div>
          <div style={{ ...D.body, fontSize: 11, color: '#7a7058', marginTop: 4, letterSpacing: '0.08em' }}>
            FILE {fileNumber} · GENERATED {fmtDate(aar.generated_at)} · v{aar.version}
          </div>
          {!isFull && aar.reveal_unlocks_at && (
            <div style={{ ...D.body, fontSize: 10, color: '#7a7058', marginTop: 4 }}>
              Reveal window: {aar.reveal_setting?.replace('_', ' ')} · unlocks {fmtDate(aar.reveal_unlocks_at)}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #cbb98c' }}>
          {[
            { id: 'brief', label: 'BRIEF' },
            { id: 'detail', label: 'DETAIL' },
          ].map(t => (
            <button key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                flex: 1, padding: '12px 16px',
                background: activeTab === t.id ? '#1f1d18' : 'transparent',
                color: activeTab === t.id ? '#c08a2a' : '#4a4636',
                border: 'none', cursor: 'pointer',
                ...D.display, fontSize: 13, letterSpacing: '0.18em', textTransform: 'uppercase',
                borderRight: t.id === 'brief' ? '1px solid #cbb98c' : 'none',
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* BRIEF tab */}
        {activeTab === 'brief' && (
          <div style={{ padding: '14px 16px', position: 'relative' }}>
            {/* § 01 — Operation Summary */}
            <SectionHeader num={1} label="Operation Summary" collapsed={collapsed.s1} onToggle={() => toggle('s1')} />
            {!collapsed.s1 && (
              <div style={{ ...D.body, fontSize: 13, color: '#1f1d18', lineHeight: 1.6 }}>
                <div><span style={D.ink3}>OPERATION: </span>{summary.event_name}</div>
                {summary.org_name && <div><span style={D.ink3}>ORG: </span>{summary.org_name}</div>}
                {summary.location && <div><span style={D.ink3}>AO: </span>{typeof summary.location === 'string' ? summary.location : (summary.location?.name || JSON.stringify(summary.location))}</div>}
                <div><span style={D.ink3}>WENT LIVE: </span>{fmtDate(summary.live_at)}</div>
                <div><span style={D.ink3}>ENDEX: </span>{fmtDate(summary.endex_at)}</div>
                <div><span style={D.ink3}>DURATION: </span>{fmtDuration(summary.duration_minutes)}{summary.total_pause_minutes > 0 ? ` (excl. ${fmtDuration(summary.total_pause_minutes)} pauses)` : ''}</div>
                <div style={{ marginTop: 8, display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                  <span><strong>{summary.total_players ?? 0}</strong> <span style={D.ink3}>operators</span></span>
                  <span><strong>{summary.faction_count ?? 0}</strong> <span style={D.ink3}>factions</span></span>
                  <span><strong>{summary.team_count ?? 0}</strong> <span style={D.ink3}>teams/patrols</span></span>
                </div>
              </div>
            )}

            {/* § 02 — Final Score (hidden under hide_faction_scores) */}
            <SectionHeader num={2} label={hideScores ? 'Faction Summary' : 'Final Score'} collapsed={collapsed.s2} onToggle={() => toggle('s2')} />
            {!collapsed.s2 && (
              <div style={{ ...D.body, fontSize: 13, color: '#1f1d18' }}>
                {factionList.length === 0 ? (
                  <p style={D.ink3}>No faction data recorded.</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(factionList.length, 3)}, 1fr)`, gap: 12 }}>
                    {factionList
                      .slice()
                      .sort((a, b) => (b.total_points || 0) - (a.total_points || 0))
                      .map(f => {
                        // Winner badge + numeric points are score data — both
                        // disappear under fog-of-war. K/D, missions, intel
                        // counts stay visible because they're activity, not
                        // ranking.
                        const isWinner = !hideScores && summary.winner_faction_id && f.faction_id === summary.winner_faction_id
                        return (
                          <div key={f.faction_id} style={{
                            border: `2px solid ${isWinner ? '#c08a2a' : '#cbb98c'}`,
                            background: isWinner ? 'rgba(192,138,42,.08)' : 'transparent',
                            padding: 12,
                          }}>
                            <p style={{ ...D.display, fontSize: 12, letterSpacing: '0.14em', color: f.color || '#4a4636' }}>{f.name}</p>
                            {!hideScores && (
                              <>
                                <p style={{ ...D.display, fontSize: 28, color: '#c08a2a', lineHeight: 1, marginTop: 4 }}>
                                  {(f.total_points || 0).toLocaleString()}
                                </p>
                                <p style={{ ...D.body, fontSize: 9, color: '#7a7058', letterSpacing: '0.14em', textTransform: 'uppercase' }}>POINTS</p>
                              </>
                            )}
                            <div style={{ ...D.body, fontSize: 11, color: '#4a4636', marginTop: hideScores ? 8 : 8 }}>
                              <div>K/D: {f.kills_confirmed || 0} / {f.deaths || 0}</div>
                              <div>MISSIONS: {f.missions_completed || 0} ✓ · {f.missions_failed || 0} ✗</div>
                              <div>INTEL: {f.intel_found || 0}</div>
                            </div>
                            {isWinner && (
                              <div style={{ ...D.display, fontSize: 10, color: '#c08a2a', letterSpacing: '0.18em', marginTop: 8, transform: 'rotate(-1deg)' }}>
                                ★ FIELD WINNER
                              </div>
                            )}
                          </div>
                        )
                      })}
                  </div>
                )}
              </div>
            )}

            {/* § 03 — Highlights */}
            <SectionHeader num={3} label="Highlights" collapsed={collapsed.s3} onToggle={() => toggle('s3')} />
            {!collapsed.s3 && (() => {
              const ph = aar.player_highlights || {}
              const renderList = (items, valueKey, valueLabel) => (
                items?.length ? (
                  <ol style={{ ...D.body, fontSize: 12, paddingLeft: 18, marginTop: 4 }}>
                    {items.map((it, i) => (
                      <li key={i} style={{ marginBottom: 2 }}>
                        <strong>{it.callsign}</strong> — {it[valueKey] || 0} {valueLabel}
                      </li>
                    ))}
                  </ol>
                ) : <p style={{ ...D.body, fontSize: 11, color: '#7a7058' }}>None recorded.</p>
              )
              return (
                <div style={{ ...D.body, fontSize: 13, color: '#1f1d18', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <p style={{ ...D.display, fontSize: 10, letterSpacing: '0.14em', color: '#4a4636' }}>TOP KILLERS</p>
                    {renderList(ph.top_killers, 'confirmed_kills', 'confirmed')}
                  </div>
                  <div>
                    <p style={{ ...D.display, fontSize: 10, letterSpacing: '0.14em', color: '#4a4636' }}>TOP INTEL</p>
                    {renderList(ph.top_intel, 'intel_count', 'finds')}
                  </div>
                  <div>
                    <p style={{ ...D.display, fontSize: 10, letterSpacing: '0.14em', color: '#4a4636' }}>TOP BOUNTIES</p>
                    {renderList(ph.top_bounties, 'bounties_claimed', 'claimed')}
                  </div>
                  <div>
                    <p style={{ ...D.display, fontSize: 10, letterSpacing: '0.14em', color: '#4a4636' }}>FACTION MVPs</p>
                    {Object.entries(ph.faction_mvps || {}).filter(([, v]) => v).length === 0 ? (
                      <p style={{ ...D.body, fontSize: 11, color: '#7a7058' }}>None recorded.</p>
                    ) : (
                      <ul style={{ ...D.body, fontSize: 12, paddingLeft: 18, marginTop: 4 }}>
                        {Object.entries(ph.faction_mvps || {}).map(([fid, mvp]) => mvp && (
                          <li key={fid}>
                            <strong style={{ color: factionMap[fid]?.color || '#4a4636' }}>{factionMap[fid]?.name || 'Faction'}:</strong> {mvp.callsign} ({mvp.score} score)
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* § 04 — Faction Summaries */}
            <SectionHeader num={4} label="Faction Summaries" collapsed={collapsed.s4} onToggle={() => toggle('s4')} />
            {!collapsed.s4 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                {factionList.length === 0 ? (
                  <p style={{ ...D.body, fontSize: 12, color: '#7a7058' }}>No factions recorded.</p>
                ) : factionList.map(f => (
                  <div key={f.faction_id} style={{ border: '1px solid #cbb98c', padding: 12, background: 'rgba(255,255,255,.4)' }}>
                    <p style={{ ...D.display, fontSize: 13, color: f.color || '#4a4636', letterSpacing: '0.1em' }}>{f.name}</p>
                    <div style={{ ...D.body, fontSize: 11, color: '#1f1d18', marginTop: 6, lineHeight: 1.7 }}>
                      {/* Point lines drop out under fog-of-war; activity
                          counts (kills, missions, intel) stay so the summary
                          still tells the story of what happened. */}
                      {!hideScores && (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={D.ink3}>Total points</span><strong>{f.total_points || 0}</strong></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={D.ink3}>Bounty points</span>{f.bounty_points || 0}</div>
                        </>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={D.ink3}>Players / teams</span>{f.player_count || 0} / {f.team_count || 0}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={D.ink3}>Missions ✓ / ✗</span>{f.missions_completed || 0} / {f.missions_failed || 0}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={D.ink3}>Kills (conf / unconf)</span>{f.kills_confirmed || 0} / {f.kills_unconfirmed || 0}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={D.ink3}>Team / faction kills</span>{f.team_kills || 0} / {f.faction_kills || 0}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={D.ink3}>Deaths</span>{f.deaths || 0}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={D.ink3}>Intel finds</span>{f.intel_found || 0}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* § 05 — Team / Node Performance */}
            <SectionHeader num={5} label="Team / Node Performance" collapsed={collapsed.s5} onToggle={() => toggle('s5')} />
            {!collapsed.s5 && (() => {
              const ranked = aar.team_highlights?.ranked || []
              if (ranked.length === 0) return <p style={{ ...D.body, fontSize: 12, color: '#7a7058' }}>No team performance data.</p>

              const factionGroups = new Map()
              ranked.forEach(n => {
                const fid = n.faction_id || '__none__'
                if (!factionGroups.has(fid)) factionGroups.set(fid, [])
                factionGroups.get(fid).push(n)
              })
              const colCount = hideScores ? 4 : 5

              return (
                <table style={{ width: '100%', borderCollapse: 'collapse', ...D.body, fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #cbb98c', color: '#7a7058', textAlign: 'left' }}>
                      <th style={{ padding: '4px 6px' }}>Callsign</th>
                      {!hideScores && <th style={{ padding: '4px 6px', textAlign: 'right' }}>Pts</th>}
                      <th style={{ padding: '4px 6px', textAlign: 'right' }}>Missions</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right' }}>K/D</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right' }}>Intel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...factionGroups.entries()].flatMap(([fid, nodes]) => {
                      const faction = fid === '__none__' ? null : factionMap[fid]
                      return [
                        <tr key={`fh-${fid}`}>
                          <td colSpan={colCount} style={{ padding: '6px 6px 2px', ...D.display, fontSize: 10, letterSpacing: '0.12em', color: faction?.color || '#7a7058', background: 'rgba(0,0,0,.04)', borderBottom: '1px solid #cbb98c' }}>
                            {faction?.name || 'NO FACTION'}
                          </td>
                        </tr>,
                        ...nodes.map(n => (
                          <tr key={n.node_id} style={{ borderBottom: '1px dashed #d4c08c' }}>
                            <td style={{ padding: '4px 6px' }}><strong>{n.callsign}</strong></td>
                            {!hideScores && <td style={{ padding: '4px 6px', textAlign: 'right' }}>{n.points || 0}</td>}
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{n.missions_completed || 0} / {(n.missions_completed || 0) + (n.missions_failed || 0)}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{n.kills || 0} / {n.deaths || 0}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{n.intel_found || 0}</td>
                          </tr>
                        )),
                      ]
                    })}
                  </tbody>
                </table>
              )
            })()}

            {/* § 06 — Personal Stat Card */}
            <SectionHeader num={6} label="Personal Stat Card" collapsed={collapsed.s6} onToggle={() => toggle('s6')} />
            {!collapsed.s6 && (() => {
              const me = aar.player_highlights?.all_players?.[user.id]
              if (!me) return <p style={{ ...D.body, fontSize: 12, color: '#7a7058' }}>No personal stats recorded for this event.</p>
              return (
                <div style={{ border: '1px solid #cbb98c', padding: 14, background: 'rgba(255,255,255,.45)' }}>
                  <p style={{ ...D.display, fontSize: 16, color: '#1f1d18', letterSpacing: '0.06em' }}>{me.callsign || me.display_name}</p>
                  <p style={{ ...D.body, fontSize: 11, color: '#7a7058' }}>{factionMap[me.faction_id]?.name || 'No faction'}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 12 }}>
                    {[
                      // Points + MVP score are score data — hidden under
                      // fog-of-war for non-GMs.
                      hideScores ? null : ['Points',     me.points_earned || 0,     '#c08a2a'],
                      ['Confirmed',  me.confirmed_kills || 0,   '#3a6a3a'],
                      ['Unconfirmed',me.unconfirmed_kills || 0, '#7a7058'],
                      ['Deaths',     me.deaths || 0,            '#a82a1f'],
                      ['Bounties',   me.bounties_claimed || 0,  '#c08a2a'],
                      ['FRAGOs',     me.fragos_participated || 0,'#2a4a8c'],
                      ['Intel',      me.intel_found || 0,       '#3a6a3a'],
                      hideScores ? null : ['MVP score',  me.mvp_score || 0,         '#1f1d18'],
                    ].filter(Boolean).map(([label, val, color]) => (
                      <div key={label} style={{ textAlign: 'center' }}>
                        <p style={{ ...D.display, fontSize: 22, color, lineHeight: 1 }}>{val}</p>
                        <p style={{ ...D.body, fontSize: 9, color: '#7a7058', letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 4 }}>{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}

            {/* Campaign seed */}
            {aar.campaign_seed && (
              <div style={{ marginTop: 18, padding: '12px 14px', background: 'rgba(192,138,42,.08)', border: '1px dashed #c08a2a' }}>
                <p style={{ ...D.display, fontSize: 10, letterSpacing: '0.14em', color: '#c08a2a', marginBottom: 4 }}>NEXT BRIEFING SEED</p>
                <p style={{ ...D.body, fontSize: 13, color: '#1f1d18', fontStyle: 'italic' }}>{aar.campaign_seed}</p>
              </div>
            )}
          </div>
        )}

        {/* DETAIL tab */}
        {activeTab === 'detail' && (
          <div style={{ padding: '14px 16px' }}>
            {/* § 01 — Event Timeline (filterable) */}
            <SectionHeader num={1} label="Event Timeline" collapsed={collapsed.d1} onToggle={() => toggle('d1')} />
            {!collapsed.d1 && (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Filter size={12} style={{ color: '#7a7058' }} />
                  <select value={filterType} onChange={e => setFilterType(e.target.value)}
                    style={{ ...D.body, fontSize: 11, padding: '4px 8px', background: '#fff', border: '1px solid #cbb98c', color: '#1f1d18' }}>
                    <option value="all">All events</option>
                    <option value="kills">Kills</option>
                    <option value="bounties">Bounties</option>
                    <option value="fragos">FRAGOs</option>
                    <option value="intel">Intel</option>
                    <option value="phase">Phases / pauses</option>
                  </select>
                  {factionList.length > 1 && (
                    <select value={filterFaction} onChange={e => setFilterFaction(e.target.value)}
                      style={{ ...D.body, fontSize: 11, padding: '4px 8px', background: '#fff', border: '1px solid #cbb98c', color: '#1f1d18' }}>
                      <option value="all">All factions</option>
                      {factionList.map(f => <option key={f.faction_id} value={f.faction_id}>{f.name}</option>)}
                    </select>
                  )}
                  <span style={{ ...D.body, fontSize: 11, color: '#7a7058', marginLeft: 'auto' }}>{filteredTimeline.length} entr{filteredTimeline.length === 1 ? 'y' : 'ies'}</span>
                </div>
                {filteredTimeline.length === 0 ? (
                  <p style={{ ...D.body, fontSize: 12, color: '#7a7058' }}>No timeline entries match the current filter.</p>
                ) : (
                  <div style={{ borderLeft: '2px solid #cbb98c', paddingLeft: 12 }}>
                    {(() => {
                      const rows = []
                      let lastDate = null
                      filteredTimeline.forEach((e, i) => {
                        const dateLabel = e.timestamp
                          ? new Date(e.timestamp).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                          : 'UNDATED'
                        if (dateLabel !== lastDate) {
                          lastDate = dateLabel
                          rows.push(
                            <div key={`dh-${dateLabel}`} style={{ ...D.display, fontSize: 10, letterSpacing: '0.14em', color: '#c08a2a', padding: '8px 0 2px', borderBottom: '1px solid rgba(203,185,140,.4)', marginBottom: 4 }}>
                              {dateLabel}
                            </div>
                          )
                        }
                        rows.push(
                          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '4px 0', ...D.body, fontSize: 12 }}>
                            <span style={{ ...D.body, fontSize: 10, color: '#7a7058', minWidth: 50 }}>{fmtTime(e.timestamp)}</span>
                            <span style={{ ...D.display, fontSize: 9, letterSpacing: '0.12em', minWidth: 90, color: EVENT_TYPE_COLOR[e.event_type] || '#4a4636', textTransform: 'uppercase' }}>
                              {e.event_type?.replace(/_/g, ' ')}
                            </span>
                            <span style={{ flex: 1, color: '#1f1d18' }}>{e.description}</span>
                          </div>
                        )
                      })
                      return rows
                    })()}
                  </div>
                )}
              </>
            )}

            {/* § 02 — Mission Dossiers */}
            <SectionHeader num={2} label="Mission Dossiers" collapsed={collapsed.d2} onToggle={() => toggle('d2')} />
            {!collapsed.d2 && (() => {
              // Build per-mission roll-ups out of the timeline. Group all
              // events with metadata.bounty_id, then label by the first
              // event's description (which carries the mission name).
              const tl = aar.timeline || []
              const groups = new Map()
              tl.forEach(e => {
                const bid = e.metadata?.bounty_id
                if (!bid) return
                if (!groups.has(bid)) groups.set(bid, [])
                groups.get(bid).push(e)
              })
              if (groups.size === 0) return <p style={{ ...D.body, fontSize: 12, color: '#7a7058' }}>No FRAGOs / bounties recorded.</p>
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[...groups.entries()].map(([bid, entries]) => {
                    const first = entries[0]
                    const last = entries[entries.length - 1]
                    const isFrago = entries.some(e => e.event_type?.startsWith('frago'))
                    return (
                      <details key={bid} style={{ border: '1px solid #cbb98c', padding: '8px 12px', background: 'rgba(255,255,255,.4)' }}>
                        <summary style={{ cursor: 'pointer', ...D.body, fontSize: 12 }}>
                          <strong>{first.description.split(' — ').slice(1).join(' — ') || first.description}</strong>
                          <span style={{ marginLeft: 8, color: '#7a7058' }}>· {isFrago ? 'FRAGO' : 'BOUNTY'} · {entries.length} events</span>
                        </summary>
                        <div style={{ marginTop: 6, ...D.body, fontSize: 11, lineHeight: 1.7 }}>
                          {entries.map((e, i) => (
                            <div key={i}>
                              <span style={{ color: '#7a7058' }}>{fmtTime(e.timestamp)} · </span>
                              <span style={{ color: EVENT_TYPE_COLOR[e.event_type] || '#4a4636', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{e.event_type?.replace(/_/g, ' ')}</span>
                              <span> — {e.description}</span>
                            </div>
                          ))}
                          <div style={{ marginTop: 4, color: '#7a7058' }}>
                            Duration: {fmtTime(first.timestamp)} → {fmtTime(last.timestamp)}
                          </div>
                        </div>
                      </details>
                    )
                  })}
                </div>
              )
            })()}

            {/* § 03 — Bounty Ledger */}
            <SectionHeader num={3} label="Bounty Ledger" collapsed={collapsed.d3} onToggle={() => toggle('d3')} />
            {!collapsed.d3 && (() => {
              const ledger = (aar.timeline || []).filter(e => e.event_type === 'bounty_issued' || e.event_type === 'bounty_claimed')
              if (ledger.length === 0) return <p style={{ ...D.body, fontSize: 12, color: '#7a7058' }}>No bounties recorded.</p>
              return (
                <div style={{ ...D.body, fontSize: 12 }}>
                  {ledger.map((e, i) => (
                    <div key={i} style={{ borderBottom: '1px dashed #d4c08c', padding: '4px 0', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{fmtTime(e.timestamp)} · {e.description}</span>
                      <span style={{ ...D.display, fontSize: 10, color: e.event_type === 'bounty_claimed' ? '#3a6a3a' : '#c08a2a', letterSpacing: '0.12em' }}>
                        {e.event_type === 'bounty_claimed' ? 'CLAIMED' : 'ISSUED'}
                      </span>
                    </div>
                  ))}
                </div>
              )
            })()}

            {/* § 04 — Full Kill Log */}
            <SectionHeader num={4} label="Full Kill Log" collapsed={collapsed.d4} onToggle={() => toggle('d4')} />
            {!collapsed.d4 && (() => {
              const kills = (aar.timeline || []).filter(e => e.event_type?.startsWith('kill') || e.event_type === 'team_kill' || e.event_type === 'faction_kill')
              if (kills.length === 0) return <p style={{ ...D.body, fontSize: 12, color: '#7a7058' }}>No kills recorded.</p>
              return (
                <div style={{ ...D.body, fontSize: 12 }}>
                  {kills.map((e, i) => (
                    <div key={i} style={{ borderBottom: '1px dashed #d4c08c', padding: '4px 0', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{fmtTime(e.timestamp)} · {e.description}</span>
                      <span style={{ ...D.display, fontSize: 10, color: EVENT_TYPE_COLOR[e.event_type] || '#4a4636', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        {e.event_type?.replace('kill_', '').replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '8px 16px', borderTop: '1px solid #cbb98c', textAlign: 'center', ...D.body, fontSize: 9, color: '#7a7058', letterSpacing: '0.18em' }}>
          ANUBIS · {fileNumber} · END OF REPORT
        </div>
      </div>
    </div>
  )
}
